<template>
  <!--
    The same shape as the guest call to action above it: round icon, heading, sentence, primary
    button, a row on a wide screen and a stack with a full width button under 767px. Those classes
    come from _guest-cta.scss and are reused rather than copied, with one modifier of its own.

    NOT the info banner from the homepage, which looks similar and means something else: that one
    sends you to the other brand, with an external link icon and an absolute url. This button turns
    something on in this browser and never navigates, so it is a button and carries no arrow.

    The order is heading, then sentence, then button, which is the reading order of the banner it
    lives in. A page that offers this viewer nothing is the exception and gets no banner at all:
    see below.
  -->
  <div
    v-if="state !== 'unavailable'"
    class="visitor-notifications guest-cta guest-cta--notifications"
    :class="{ 'visitor-notifications--bare': bare }"
    data-test="visitor-notifications"
    :data-state="state"
    :aria-busy="busy !== null ? 'true' : 'false'"
  >
    <div class="guest-cta__container">
      <div class="guest-cta__icon-holder" aria-hidden="true">
        <i class="icon-notification"></i>
      </div>

      <div class="guest-cta__content">
        <h3
          v-if="! bare"
          class="guest-cta__title visitor-notifications__title"
          data-test="visitor-notifications-title"
        >{{ title }}</h3>

        <p
          v-if="state === 'unsupported'"
          class="guest-cta__text visitor-notifications__notice"
          data-test="visitor-notifications-unsupported"
        >{{ statusLine }}</p>

        <p
          v-else
          ref="status"
          :id="statusId"
          class="guest-cta__text visitor-notifications__status"
          data-test="visitor-notifications-status"
          role="status"
          aria-live="polite"
          tabindex="-1"
        >{{ statusLine }}</p>

        <!--
          Only ever drawn after the worker says it drew something. It names no control, so it does
          not owe the page a button, and it never changes the state: this is a receipt and not a
          state of its own.
        -->
        <p
          v-if="drewAt"
          class="guest-cta__text visitor-notifications__drew"
          data-test="visitor-notifications-drew"
          role="status"
          aria-live="polite"
        >{{ drewLine }}</p>
      </div>

      <div v-if="controls.length" class="guest-cta__action">
        <button
          v-if="controls.indexOf('toggle') !== -1"
          type="button"
          ref="toggle"
          class="visitor-notifications__toggle guest-cta__button theme-btn"
          :class="{ 'theme-btn--primary': primaryControl === 'toggle' }"
          data-test="visitor-notifications-toggle"
          :disabled="permission === 'denied' && !hasSubscription"
          :aria-disabled="busy !== null ? 'true' : 'false'"
          :aria-describedby="statusId"
          @click="onToggle"
        >{{ toggleLabel }}</button>

        <button
          v-if="controls.indexOf('retry') !== -1"
          type="button"
          ref="retry"
          class="visitor-notifications__retry guest-cta__button theme-btn"
          :class="{ 'theme-btn--primary': primaryControl === 'retry' }"
          data-test="visitor-notifications-retry"
          :aria-disabled="busy !== null || rechecking ? 'true' : 'false'"
          :aria-describedby="statusId"
          @click="onRetry"
        >{{ copy.retry }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Notifications for a guest, in the browser tab, without an account.
 *
 * Three rules hold this together and each of them is a rule the account-side component breaks.
 *
 * It registers its own worker under its own scope and only ever speaks to the handle that
 * register() returned. It never reads navigator.serviceWorker.ready, which resolves to whatever
 * worker controls the page and on this site is the Pusher Beams worker at the root. A guest has
 * no account, so a guest never reaches Beams at all.
 *
 * It decides nothing itself. Every state comes from visitorPushUiState(), a pure function of
 * what this browser holds and what the server said, so the ways the control can lie are a table
 * somebody can read rather than a path through a method.
 *
 * And it leaves no orphans. A subscription the server refused to store is rolled back in the
 * browser, because a browser holding a live subscription nothing on the server knows about is
 * exactly the state that used to read as on.
 */
import {
  SUCCESS,
  TIMEOUT,
  VISITOR_PUSH_DEADLINE_MS,
  deadlineExpired,
  deleteOnServer,
  readKnownFromServer,
  subscribeOnServer,
  withDeadline,
} from '../helpers/visitorPushTransport';
import {
  SUPPORTED,
  UNSUPPORTED_IOS_TAB,
  detectVisitorPushSupport,
} from '../helpers/visitorPushSupport';
import { visitorPushControlsFor, visitorPushUiState } from '../helpers/visitorPushState';
import { visitorPushBrandIn, visitorPushCopyFor, visitorPushTitleFor } from '../helpers/visitorPushCopy';

/** The browser would not say what it holds. Distinct from holding nothing. */
const UNREADABLE = Symbol('visitor-push-browser-unreadable');

export const VISITOR_WORKER_URL = '/visitor-push/service-worker.js';
export const VISITOR_WORKER_SCOPE = '/visitor-push/';

/** How long a re-read stands for, when a tab is looked at again. */
export const VISITOR_PUSH_REREAD_MS = 5000;

/**
 * How often the activation wait looks again while nothing has told it to.
 *
 * A first click on a cold browser can land on a registration that answers with active, installing
 * and waiting all empty. There is no event to listen to in that state, because the worker that
 * would fire statechange does not exist yet, so the only way to learn that it changed is to look.
 */
export const VISITOR_PUSH_ACTIVATION_POLL_MS = 250;

/**
 * How long the first click keeps asking the server to store a subscription the browser has
 * already made.
 *
 * A budget equal to VISITOR_PUSH_DEADLINE_MS would be decoration rather than a retry: a TIMEOUT
 * verdict means that deadline has by definition already been spent, so there would never be room
 * for a second attempt. This one is a multiple of it and is spent in whole attempts. Asking twice
 * is safe because the endpoint is idempotent on endpoint_hash: VisitorPushSubscriptionController
 * stores through firstOrNew and handles the unique race itself.
 */
export const VISITOR_PUSH_STORE_BUDGET_MS = 2 * VISITOR_PUSH_DEADLINE_MS;

export default {
  name: 'VisitorNotifications',

  props: {
    websiteId: { type: Number, required: true },
    locale: { type: String, default: 'nl' },
    vapidPublicKey: { type: String, default: '' },
    // The sentences for this locale, built server side from the visitor-push translation group.
    // Null means the page did not say, and then the bundled copy is used. See the copy computed.
    serverCopy: { type: Object, default: null },
    // Of deze server iets kan ondertekenen: onderwerp, publieke sleutel en privesleutel alle
    // drie aanwezig. Een serverfeit, meegegeven door de pagina, en niet iets wat de client uit
    // een sleutel afleidt.
    //
    // Waarom dit er is. De publieke sleutel is een derde van wat de verzender eist, en de
    // component noemde zichzelf geconfigureerd zodra die er was. Dan staat er een volle
    // uitnodiging, drukt de bezoeker, geeft de browser echte toestemming, en weigert subscribe
    // met 503 omdat het onderwerp of de privesleutel ontbreekt. De bezoeker eindigt op "Herstel
    // nodig" met een knop die nooit kan slagen, en de toestemmingsvraag is per origin en blijft
    // plakken. Iets onomkeerbaars vragen in ruil voor iets dat niet kan is de duurste vorm van
    // een onwaar gezicht, en de desk waarschuwt er woordelijk voor op zijn eigen pagina.
    //
    // De standaard is false en dat is met opzet de gesloten kant: een pagina die dit niet zegt,
    // nodigt niemand uit. Wie al is aangemeld houdt zijn uitgang, want de toestandsfunctie laat
    // de tak voor "hier wordt niets aangeboden" wijken voor uitzetten en voor herstel.
    deliverable: { type: Boolean, default: false },
    // EXIT-A. True when this viewer is outside the audience of this host. The component still
    // mounts, and may then only look: probe for a registration that already exists, never make
    // one, and offer the way out to somebody who already said yes. See boot().
    exitOnly: { type: Boolean, default: false },
    // The brand this page is served under, for the two sentences that name it. Empty falls back to
    // a generic word rather than to a brand, because a wrong name is worse than no name.
    brandName: { type: String, default: '' },
  },

  data() {
    return {
      support: 'unsupported',
      permission: 'default',
      busy: null,
      browserUnreadable: false,
      subscriptionForAnotherKey: false,
      lastReadAt: null,
      hasSubscription: false,
      // Three valued on purpose. true = the server holds a row, false = it definitely does not,
      // null = it could not be asked. Only the first of those three may read as on.
      known: false,
      lastTurnOnFailed: false,
      // Set when a turn off ended with the browser still holding the subscription. Repair is
      // reached from both directions and the two want opposite things next: somebody whose turn
      // on failed wants it on, somebody whose turn off failed asked to be left alone.
      lastTurnOffFailed: false,
      registration: null,
      subscription: null,
      // The endpoint of a browser subscription that is already gone while the server has not
      // confirmed it forgot the row. Kept so the retry can finish the half that is left; without
      // it, pressing "Opnieuw proberen" finds no subscription, reports off, and never asks the
      // server again, which is the half-done off this component exists to remove.
      unconfirmedEndpoint: null,
      // A recheck is neither turning on nor turning off, so it does not change the state, but it
      // must not be startable twice: two in flight can settle in either order and the older
      // answer would win.
      rechecking: false,
      // What the worker told this tab it just drew, if anything. Only ever set from a message the
      // visitor worker posts after showNotification resolved, so it says something the browser has
      // already done rather than something this page hopes for.
      drewAt: null,
      drewBody: '',
      // How many notifications the browser was holding just after the worker drew one. Null means
      // it could not be asked, which is not the same as zero and is never printed as zero.
      drewCards: null,
      // The controls as they were when the current piece of work started, or null when nothing is
      // under way, and which of them carried the emphasis. See freezeControls.
      controlsWhileBusy: null,
      primaryWhileBusy: null,
      // Counts the presses that decide something. A read that was already in flight when one
      // happened is stale, and stale answers may not be written. See recheck.
      pressGeneration: 0,
      // Set when the component is torn down. The activation wait below is the only loop in this
      // file that can still be running at that moment, and a loop that keeps polling a browser
      // for a control nobody is looking at any more is a leak with a deadline on it.
      gone: false,
    };
  },

  computed: {
    /**
     * The sentence this control is currently showing, addressed by id.
     *
     * Both buttons point at it, because "Opnieuw proberen" on its own says nothing about what
     * went wrong, and the id has to be unique: the control renders on the homepage and on the
     * listings page, and a page could hold two of them.
     */
    statusId() {
      return 'visitor-notifications-status-' + this._uid;
    },
    configured() {
      // Allebei. De sleutel is wat een abonnement mee gemaakt wordt, dus zonder sleutel kan de
      // browser niets; het serverfeit is of er daarna ook iets verstuurd kan worden. Een van de
      // twee is niet genoeg, en het was er een.
      return this.deliverable
        && typeof this.vapidPublicKey === 'string'
        && this.vapidPublicKey.length > 0;
    },
    state() {
      return visitorPushUiState({
        excluded: this.exitOnly,
        configured: this.configured,
        support: this.support,
        permission: this.permission,
        busy: this.busy,
        browserUnreadable: this.browserUnreadable,
        subscriptionForAnotherKey: this.subscriptionForAnotherKey,
        hasSubscription: this.hasSubscription,
        known: this.known,
        lastTurnOnFailed: this.lastTurnOnFailed,
        // Which direction the last press was, for the one case where the facts alone cannot say:
        // a turn off both halves refused looks exactly like an ordinary subscription the server
        // could not be asked about.
        lastTurnOffFailed: this.lastTurnOffFailed,
        // Only ever set by a turn off whose server half went unanswered, and cleared the moment
        // the server says the row is gone. It is what keeps a half finished off on the screen in
        // the two branches that would otherwise draw nothing at all.
        unconfirmedEndpoint: this.unconfirmedEndpoint,
      });
    },
    /**
     * Something a press started is still running.
     *
     * Two kinds. Busy is a press that will change the state, and that is what the control list
     * is frozen for. Rechecking is a press that only asks a question, so nothing moves while it
     * runs and freezing would buy nothing; what it shares with the other kind is the landing,
     * where the answer arrives and a control can go. That is what this is read for: where the
     * focus lands afterwards, which is the same question for both.
     */
    working() {
      return this.busy !== null || this.rechecking;
    },
    controls() {
      // Nothing moves while work is under way. See freezeControls.
      if (this.busy !== null && this.controlsWhileBusy !== null) {
        return this.controlsWhileBusy;
      }

      // The same facts the state came from. Two of the branches in there reach a state whose
      // ordinary control list would offer to turn notifications on in a place that offers
      // nothing, and the list cannot tell from the state alone.
      return visitorPushControlsFor(this.state, {
        excluded: this.exitOnly,
        configured: this.configured,
        hasSubscription: this.hasSubscription,
        known: this.known,
      });
    },
    /**
     * No banner where nothing is offered.
     *
     * This used to ask whether the state was 'excluded', which was the same question for as long
     * as excluded was the only state those two branches could reach. It is not any more: a viewer
     * outside the audience and a visitor on a keyless server both reach the cleanup state now,
     * and with the old rule that brought back the icon, the frame and the heading "Meldingen van
     * {brand} in deze browser" for a brand that is offering this person nothing.
     *
     * So it asks the thing it means. Somebody finishing an unsubscribe gets the sentence and the
     * button and no invitation around them.
     */
    bare() {
      return this.exitOnly || ! this.configured;
    },
    /**
     * Which button the eye lands on.
     *
     * In the states that carry both and disagree, the toggle is not what the visitor is waiting
     * for. On a half finished off it offers to turn notifications on, next to the retry that
     * finishes the turning off they already asked for, so the emphasised button was the one that
     * abandons the cleanup. Repair is the same shape, where the sentence itself points at the
     * retry. On-unverified is not one of them: there the toggle is still the action and the retry
     * only asks the server again.
     */
    primaryControl() {
      // Frozen with the list, for the same reason: the emphasis moving under a finger is the
      // same surprise as a button moving. On a half finished off the retry carries it, and
      // during the call the plain rule would hand it back to the toggle.
      if (this.busy !== null && this.primaryWhileBusy !== null) {
        return this.primaryWhileBusy;
      }

      // One button is the primary one, whichever it is. Where nothing is offered the retry is
      // drawn alone, and asking for the toggle there left the only button on the screen without
      // the emphasis it is the only candidate for.
      if (this.controls.length === 1) {
        return this.controls[0];
      }
      if (this.controls.indexOf('retry') !== -1
        && (this.state === 'off-cleanup-pending' || this.state === 'repair')) {
        return 'retry';
      }

      return 'toggle';
    },
    copy() {
      // The page wins when it says something. resources/js/helpers/visitorPushCopy.js used to be
      // the only place these sentences lived, which is why a visitor on /en already read English:
      // it came out of the bundle and not out of the translation table. The partial now builds
      // this object from trans() for the current locale and hands it in, so changing a sentence
      // is a Publish in the translation manager and not a new image.
      //
      // The bundle stays as the fallback and not as a second source of truth. A unit test mounts
      // this component without a server and has to keep reading the same sentences, and a page
      // that somehow renders without the prop should show text rather than nothing.
      return this.serverCopy || visitorPushCopyFor(this.locale);
    },
    title() {
      return visitorPushBrandIn(visitorPushTitleFor(this.copy, this.state), this.brandName, this.locale, this.copy);
    },
    /**
     * The sentence under the heading, with the brand filled in where one is named.
     *
     * One state has two sentences, because one fact changes what the visitor can do with it.
     * Blocked while still subscribed draws the off button, and the ordinary blocked sentence
     * explains how to turn notifications on in the browser settings: an answer to a question this
     * person is not asking, above the only button they can press.
     */
    statusLine() {
      const key = this.state === 'blocked' && this.hasSubscription ? 'blocked-subscribed' : this.state;

      return visitorPushBrandIn(this.copy.states[key], this.brandName, this.locale, this.copy);
    },
    drewLine() {
      if (!this.drewAt) {
        return '';
      }

      return this.copy.drew
        .replace('{time}', this.drewAt)
        .replace('{body}', this.drewBody)
        .replace('{cards}', this.drewCards === null ? '?' : String(this.drewCards));
    },
    /**
     * Off when this browser holds something, on otherwise. That stays true while the browser is
     * blocking notifications: permission is what subscribe needs, and turning off is a delete on
     * the server plus an unsubscribe here, neither of which asks the browser for permission.
     * A visitor who revoked permission while subscribed used to get a greyed out button and no
     * way off the list at all, while the row stayed and the desk kept counting them. The condition
     * reads the permission rather than the state, because the half finished off is a state of its
     * own now and the button there would otherwise offer to turn on what this browser refuses.
     */
    toggleLabel() {
      // While something is under way, the label is the direction of that work rather than a
      // reading of what the browser holds right now. Turning off drops the subscription before
      // the server answers, so for the whole of that call the button read "Meldingen aan" under
      // the sentence "Bezig met uitzetten", offering the opposite of what was just pressed. It is
      // inert in that moment, and it is still the loudest thing on the screen.
      if (this.busy === 'turning-off') {
        return this.copy.turnOff;
      }
      if (this.busy === 'turning-on') {
        return this.copy.turnOn;
      }

      return this.hasSubscription ? this.copy.turnOff : this.copy.turnOn;
    },
  },

  watch: {
    /**
     * Where the focus goes when the work lands.
     *
     * The controls are held still for the length of a press, and then the work finishes and the
     * state changes for real: a control that is no longer right goes, and in two of those
     * landings it is the one that was pressed. A button that leaves the document hands its focus
     * to the body, so somebody on a keyboard is put back at the top of the page at the exact
     * moment they were told something. This puts it somewhere deliberate instead: the control
     * that is still there, or the sentence that just changed, which carries the answer.
     *
     * Only when the focus was inside this control to begin with. Somebody who pressed the button
     * and then clicked elsewhere is not dragged back.
     */
    working(next, previous) {
      if (previous !== true || next !== false || typeof document === 'undefined') {
        return;
      }
      const root = this.$el;
      const wasHere = root && root.contains(document.activeElement);
      if (!wasHere) {
        return;
      }

      this.$nextTick(() => {
        const active = typeof document !== 'undefined' ? document.activeElement : null;
        if (root && root.contains(active) && active !== document.body) {
          return;
        }
        const toggle = this.$refs.toggle;
        const retry = this.$refs.retry;
        const target = (toggle && !toggle.disabled && toggle) || (retry && retry) || this.$refs.status;
        if (target && typeof target.focus === 'function') {
          target.focus();
        }
      });
    },
  },

  mounted() {
    this.boot();

    // Two tabs, one browser. Turning notifications off in one leaves the other saying
    // "Meldingen aan" until somebody reloads it, and the next thing that tab does with that button
    // is act on a subscription that is not there any more. A tab that has just been looked at
    // again is the cheapest moment to re-read both halves, and the only one a visitor notices.
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', this.onVisible);
    }

    // The worker already tells any open tab of this site when it has drawn a diagnostic. Nothing
    // was listening, so a test push that the browser accepted and the worker handled left no trace
    // anywhere a person could look, and a silent screen had to stand for every failure at once.
    if (typeof navigator !== 'undefined' && navigator.serviceWorker
      && typeof navigator.serviceWorker.addEventListener === 'function') {
      navigator.serviceWorker.addEventListener('message', this.onWorkerMessage);
    }
  },

  beforeDestroy() {
    this.gone = true;

    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener('visibilitychange', this.onVisible);
    }

    if (typeof navigator !== 'undefined' && navigator.serviceWorker
      && typeof navigator.serviceWorker.removeEventListener === 'function') {
      navigator.serviceWorker.removeEventListener('message', this.onWorkerMessage);
    }
  },

  methods: {
    /**
     * What the worker drew, said out loud in the page.
     *
     * The worker posts this from inside the push handler, after showNotification has resolved, so
     * a line here is evidence that the browser was handed a notification to draw. It is not
     * evidence that anything appeared on screen: that is the next thing along and the two are now
     * possible to tell apart, which is the whole reason this listener exists.
     *
     * Only the diagnostic is mirrored, and only our own worker posts it. Anything else on this
     * channel is ignored rather than rendered.
     */
    async onWorkerMessage(event) {
      const data = event && event.data;
      if (!data || data.type !== 'visitor-push-diagnostic') {
        return;
      }

      this.drewBody = typeof data.body === 'string' ? data.body : '';
      this.drewAt = new Date().toLocaleTimeString();

      // And how many notifications this registration is actually holding, straight after the
      // worker said it drew one. The two answers together split what a silent screen cannot:
      // a card the browser holds and did not surface is a different failure from a
      // showNotification that resolved and left nothing behind.
      //
      // The registration this asks is the one this component already has. It does not go looking
      // for another: an unscoped getRegistration is forbidden here and there is exactly one
      // scoped call in this file, in the exit probe.
      this.drewCards = await this.heldNotificationCount();
    },

    /** How many notifications the visitor registration is holding, or null when it cannot say. */
    async heldNotificationCount() {
      const registration = this.registration;
      if (!registration || typeof registration.getNotifications !== 'function') {
        return null;
      }

      try {
        const held = await withDeadline(registration.getNotifications(), VISITOR_PUSH_DEADLINE_MS);

        return Array.isArray(held) ? held.length : null;
      } catch (error) {
        return null;
      }
    },

    /**
     * Re-read the truth when this tab comes back into view, and only then.
     *
     * Not while something is in flight, because the answer is already being fetched and a second
     * read would race it. Not when the tab is being hidden, because nobody is looking. And never
     * awaited from here: a visibility handler that waits is a handler that blocks the event.
     */
    onVisible() {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      if (this.busy !== null || this.rechecking) {
        return;
      }

      // Not more often than once every few seconds. Coming back to a tab is worth a read; a burst
      // of visibility events, which a browser can produce for reasons of its own, is worth one
      // read between them. The server would answer all of them and the rate limit would eventually
      // refuse one, which a visitor would read as repair for no reason at all.
      const now = Date.now();
      if (this.lastReadAt !== null && now - this.lastReadAt < VISITOR_PUSH_REREAD_MS) {
        return;
      }
      this.lastReadAt = now;

      this.boot();
    },

    /**
     * The one call in this file that asks the browser for a registration that already exists.
     *
     * Two paths need it and neither may grow its own. EXIT-A looks for a registration rather than
     * making one, and the activation wait looks again when a fresh register() answered with
     * nothing running and nothing pending. Keeping it in one method is what keeps the rule
     * checkable: the scope is in the call, every time, because an unscoped getRegistration
     * answers with whatever controls the page and on this site that is the Beams worker at the
     * root. A browser that throws or will not answer in time is an unknown, never a hang.
     */
    async scopedRegistration() {
      if (typeof navigator === 'undefined' || !navigator.serviceWorker
        || typeof navigator.serviceWorker.getRegistration !== 'function') {
        return null;
      }

      try {
        const found = await withDeadline(
          navigator.serviceWorker.getRegistration('/visitor-push/'),
          VISITOR_PUSH_DEADLINE_MS,
          null,
        );
        if (!found || deadlineExpired(found)) {
          return null;
        }

        return found;
      } catch (error) {
        return null;
      }
    },

    /**
     * EXIT-A. Look, and do nothing else.
     *
     * The ordinary boot path registers a worker before it knows anything: mounted calls boot,
     * boot calls ensureRegistration, and that calls navigator.serviceWorker.register. For a
     * viewer who is outside the audience that would put a worker on their machine during a visit
     * that never invited them to anything.
     *
     * So this path never registers. It asks for a registration that is already there, under its
     * scope, because an unscoped getRegistration is forbidden here and would read as a licence
     * for exactly the call this avoids. No registration means no network call at all. An
     * endpoint means exactly one known call, which the existing rate limit on that endpoint
     * already covers, and the way out is only drawn once the server confirms the row is real.
     *
     * Permission is never requested, nothing is subscribed, and no diagnostic is fired. The
     * server refuses those anyway; this is about not asking.
     */
    async probeForExit() {
      const generation = this.pressGeneration;
      const registration = await this.scopedRegistration();
      if (this.supersededSince(generation)) {
        return;
      }
      if (!registration || !registration.pushManager) {
        await this.resolveUnfinishedOff();

        return;
      }

      const subscription = await this.heldSubscription(registration);
      if (this.supersededSince(generation)) {
        return;
      }
      if (!subscription || subscription === UNREADABLE || !subscription.endpoint) {
        await this.resolveUnfinishedOff();

        return;
      }

      this.registration = registration;
      this.subscription = subscription;
      this.hasSubscription = true;
      const answer = await this.askServer(subscription.endpoint);
      if (this.supersededSince(generation)) {
        return;
      }
      this.known = answer;
    },

    /**
     * True when a press happened since this read started, so its answer is about the situation
     * before that press and may not be written.
     *
     * The counter was introduced for the recheck, which was the case somebody found. It guards
     * every write that lands after an await now, and there are seven of them: boot pins once and
     * checks after each of its three waits, the exit probe does the same, and the unfinished off
     * checks after its one. A successful turn off overtaken by a boot that was already on its way
     * ended at repair with nothing marking the direction, and the retry there turns on, which
     * subscribes the person who just turned off. Same axis, same rule, everywhere it applies.
     */
    supersededSince(generation) {
      return generation !== this.pressGeneration;
    },

    async boot() {
      // Every boot decides this again. It used to be set here and cleared only by turnOn or
      // turnOff, both of which need a press, so one slow read on a tab coming back from the
      // background flipped a working subscriber to "Herstel nodig" and left it there through every
      // later successful re-read. A visitor who touched nothing was told to repair something, and
      // the only way out was the button. That is the face this component was written to remove.
      this.browserUnreadable = false;
      const generation = this.pressGeneration;

      if (this.exitOnly) {
        await this.probeForExit();

        return;
      }


      if (!this.configured) {
        // Nothing to subscribe against. Registering a worker for a control that will never be
        // drawn leaves a worker on the visitor's machine for no reason at all.
        //
        // Look anyway, along the EXIT-A path, which registers nothing and asks the network
        // nothing unless it finds an endpoint that is already there. Keys get pulled and rotated,
        // and this used to return here: a visitor who said yes yesterday kept their row, kept
        // being counted on the desk, and lost every control on the page at once. The way out does
        // not need a key, because a delete is not signed with one.
        await this.probeForExit();

        return;
      }
      this.support = detectVisitorPushSupport({});
      if (this.support !== SUPPORTED) {
        return;
      }
      this.permission = this.readPermission();

      const registration = await this.ensureRegistration();
      if (this.supersededSince(generation)) {
        return;
      }
      if (!registration) {
        this.browserUnreadable = true;
        return;
      }

      const existing = await this.heldSubscription(registration);
      if (this.supersededSince(generation)) {
        return;
      }
      if (existing === UNREADABLE) {
        this.browserUnreadable = true;
        return;
      }
      if (!existing) {
        this.hasSubscription = false;

        if (await this.resolveUnfinishedOff()) {
          return;
        }

        // Nothing to ask the server about. There is no endpoint, so there is no row, and known
        // false is the honest answer rather than a call with no subject.
        this.known = false;
        return;
      }

      this.subscription = existing;
      this.hasSubscription = true;
      this.subscriptionForAnotherKey = !this.subscriptionMatchesKey(existing);
      const answer = await this.askServer(existing.endpoint);
      if (this.supersededSince(generation)) {
        return;
      }
      this.known = answer;
    },

    /**
     * Ask again about a turn off the server never confirmed, and say whether there was one.
     *
     * The browser holds nothing at this point, so there is nothing to read here, and that
     * endpoint is the only thing left that says which row to forget. Writing known false flat
     * instead is what made one tab switch turn "still being cleaned up" into "off": the retry
     * left the screen and the row stayed on the server with the desk still counting it.
     *
     * Both paths into this component need it. The ordinary boot calls it, and so does EXIT-A,
     * which returns as soon as the browser holds nothing and would otherwise leave a viewer
     * outside the audience on that banner through every tab switch for the rest of the session.
     * It costs one known call and only ever when this page view already pressed off.
     */
    async resolveUnfinishedOff() {
      if (! this.unconfirmedEndpoint) {
        return false;
      }

      const generation = this.pressGeneration;
      const answer = await this.askServer(this.unconfirmedEndpoint);
      if (this.supersededSince(generation)) {
        return true;
      }
      this.known = answer;
      if (this.known === false) {
        this.unconfirmedEndpoint = null;
      }

      return true;
    },

    /** true, false, or null when the server could not be asked. */
    async askServer(endpoint) {
      const verdict = await readKnownFromServer({ endpoint, websiteId: this.websiteId });

      return verdict.outcome === SUCCESS ? verdict.known : null;
    },

    readPermission() {
      const api = typeof window !== 'undefined' ? window.Notification : null;

      return api && api.permission ? api.permission : 'default';
    },

    /**
     * The one and only registration this component will ever speak to. Registered by explicit
     * path and explicit scope so it cannot become the controller of the site and cannot be
     * confused with the Beams worker at the root.
     */
    async ensureRegistration() {
      if (this.registration) {
        return this.registration;
      }
      try {
        const visitorRegistration = await withDeadline(
          navigator.serviceWorker.register(
            '/visitor-push/service-worker.js',
            { scope: '/visitor-push/' }
          ),
          VISITOR_PUSH_DEADLINE_MS,
          null,
        );
        if (!visitorRegistration || deadlineExpired(visitorRegistration)) {
          // A registration that never resolves is a real state on a worker that failed to fetch
          // or failed to parse. Unknown is a usable answer; a control that spins forever is not.
          return null;
        }
        this.registration = visitorRegistration;
        return visitorRegistration;
      } catch (error) {
        return null;
      }
    },

    /**
     * What this browser currently holds, or UNREADABLE when it would not say in time.
     *
     * Every promise in this path is one a browser can decline to settle, and this one sits behind
     * a service worker that may never activate. A lost race is an answer here, never a hang.
     */
    async heldSubscription(registration) {
      try {
        const held = await withDeadline(
          registration.pushManager.getSubscription(),
          VISITOR_PUSH_DEADLINE_MS,
          null,
        );
        if (deadlineExpired(held)) {
          return UNREADABLE;
        }
        return held || null;
      } catch (error) {
        return UNREADABLE;
      }
    },

    /**
     * Waits until this registration has a worker running, or until the deadline says it never
     * will. A registration without an active worker cannot be subscribed against.
     *
     * The first click on a cold browser is what this used to get wrong, and it got it wrong in
     * the one place nobody looks. register() can settle with active, installing and waiting all
     * three empty. That is not a broken browser: it is a registration the browser has accepted
     * and not started installing yet. The old code read those three empty slots as "there is
     * nothing to wait for" and returned false, so the very first press fell straight through to
     * the repair screen without ever calling subscribe. The visitor had just said Allow and was
     * told to try again, and pressing again worked, which is exactly why it read as flaky rather
     * than as a bug.
     *
     * There is nothing to listen to in that state, because the worker whose statechange would
     * fire does not exist yet. So this looks again on its own clock and takes whichever answer
     * arrives first: a pending worker changing state, or the scoped registration having grown an
     * active one. A browser that throws while being asked is asked again rather than believed,
     * which is what makes this survive an InvalidStateError inside one and the same click.
     *
     * The registration this settles on is kept, so subscribe runs against the handle that was
     * actually found to be active. That is either the one register() returned or the one the
     * scoped getRegistration answered with, and never any other.
     */
    async waitForActive(registration) {
      const until = Date.now() + VISITOR_PUSH_DEADLINE_MS;
      let current = registration;

      while (!this.gone) {
        if (current && current.active) {
          this.registration = current;

          return true;
        }
        if (Date.now() >= until) {
          return false;
        }

        await this.activationTick(current);
        const reread = await this.scopedRegistration();
        if (reread) {
          current = reread;
        }
      }

      return false;
    },

    /**
     * One round of waiting. It resolves the moment a pending worker changes state, and otherwise
     * on a timer it owns, so it always settles and never needs a deadline wrapped around it.
     */
    activationTick(registration) {
      const pending = registration ? (registration.installing || registration.waiting) : null;

      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) {
            return;
          }
          settled = true;
          resolve(null);
        };

        if (pending && typeof pending.addEventListener === 'function') {
          pending.addEventListener('statechange', finish);
        }
        setTimeout(finish, VISITOR_PUSH_ACTIVATION_POLL_MS);
      });
    },

    /**
     * Pressing while the last press is still in flight does nothing, and this guard is the only
     * thing that makes that true.
     *
     * The button used to carry `disabled` while busy, and a disabled button that has focus hands
     * its focus to the document body. That happens exactly when the sentence underneath it changes,
     * so somebody using a keyboard or a screen reader loses their place at the one moment they
     * needed to hear something. Measured in Chromium: focus before the press was the button,
     * during the press it was BODY.
     *
     * So while busy the button stays focusable and says aria-disabled, and this decides what a
     * second press does. A second subscribe would make a second browser subscription for one
     * visitor; a second delete would race the first.
     */
    onToggle() {
      if (this.busy !== null) {
        return;
      }
      if (this.hasSubscription) {
        this.turnOff();
        return;
      }
      this.turnOn();
    },

    /**
     * Hold the buttons still for the length of the work.
     *
     * Pressing a control changes the state, and the state decides which controls are drawn, so a
     * button can leave the screen under the finger that pressed it.
     *
     * WHAT THIS DOES NOT COVER, said here because this is where somebody will look. The freeze
     * holds for the length of the press and not for the landing: when the work finishes the state
     * changes for real, and a control that is no longer right goes, which is the whole point. In
     * two of those landings the button that was pressed is the one that goes, and focus falls to
     * the document body again. Moving focus deliberately at that moment is the fix, and it is a
     * change to how this control behaves rather than to what it draws, so it is written down
     * rather than guessed at. That is not a style problem:
     * a disabled or removed button hands its focus to the document body, which is what happens to
     * somebody on a keyboard at the exact moment they act. It was fixed once for the two branches
     * that offer nothing and the ordinary page had the same hole, so this is the rule rather than
     * a third special case: while something is under way, the list of controls is the one that was
     * on the screen when it started.
     */
    freezeControls() {
      this.controlsWhileBusy = this.controls;
      this.primaryWhileBusy = this.primaryControl;
      // Anything already in flight belongs to the situation before this press.
      this.pressGeneration += 1;
    },

    onRetry() {
      if (this.busy !== null || this.rechecking) {
        return;
      }
      if (this.state === 'on-unverified') {
        this.recheck();
        return;
      }
      if (this.state === 'off-cleanup-pending') {
        this.turnOff();
        return;
      }
      // Repair after a turn off that the browser refused. This person pressed "Meldingen uit" and
      // was answered with "Herstel nodig", and the button the sentence points at used to fall
      // through to turnOn and subscribe them again. Finish what they asked for instead.
      //
      // The unconfirmed endpoint is the second way into the same situation. A turn off that the
      // browser finished and the server did not leaves repair reachable through one unreadable
      // read, and lastTurnOffFailed is false there because the browser did let go. Retrying by
      // turning on would subscribe somebody who pressed off, which is the one thing this button
      // may never do.
      // The note is the part that needs the extra condition, and only the note. It survives a
      // tab; the browser's subscription does not belong to a tab. So a second tab that presses
      // on leaves this one carrying a note about somebody who is now subscribed, and reading
      // that note raw sent the button under "Herstel nodig" to turn off a visitor who is on.
      // What the note means is "there may be a row nobody is using"; it never means
      // "unsubscribe this browser". A refused turn off is different: the visitor asked to
      // leave and the browser is still holding on, so finishing is the right answer whether or
      // not it is still held.
      //
      // Written as one condition rather than two. The first version was two branches that
      // overlapped on exactly the refused turn off, and the harness said so out loud: take the
      // first branch away and the second one caught the same tests, so two mutations survived
      // and the tests behind them measured nothing.
      const finishLeaving = this.lastTurnOffFailed
        || (this.unconfirmedEndpoint && ! this.hasSubscription);

      if (this.state === 'repair' && finishLeaving) {
        this.turnOff();
        return;
      }
      this.turnOn();
    },

    async turnOn() {
      this.freezeControls();
      this.busy = 'turning-on';
      this.lastTurnOnFailed = false;
      this.lastTurnOffFailed = false;
      this.browserUnreadable = false;
      // The record that a cleanup is owed is NOT dropped here, and the first version of this
      // repair did drop it here, one line above the permission prompt. Somebody who then
      // dismissed or refused that prompt lost the only string this page still had to finish
      // the cleanup with: the row stayed on the server, the desk kept counting it, and the
      // page had nothing left to ask about. It is dropped at the two landings instead, where
      // the visitor really is subscribed again.

      if (this.readPermission() !== 'granted') {
        let granted = 'denied';
        try {
          granted = await window.Notification.requestPermission();
        } catch (error) {
          granted = 'denied';
        }
        this.permission = granted;
        if (granted !== 'granted') {
          // Nothing is sent to the server. A row for a browser that will never accept a
          // notification is a row that can only ever fail.
          this.busy = null;
          return;
        }
      }

      const registration = await this.ensureRegistration();
      if (!registration || !(await this.waitForActive(registration))) {
        return this.turnOnFailed();
      }

      // The one the wait settled on, which is not always the one ensureRegistration handed back.
      // On a cold first click the wait re-reads the scoped registration until it finds an active
      // worker, and it keeps what it found. Subscribing against the earlier handle would be
      // subscribing against a registration this code has just decided it could not use, and the
      // comment on waitForActive already claimed this was happening. It was not.
      const active = this.registration || registration;

      await this.releaseForeignSubscription(active);

      let subscription;
      try {
        const options = { userVisibleOnly: true };
        if (this.vapidPublicKey) {
          options.applicationServerKey = this.urlBase64ToUint8Array(this.vapidPublicKey);
        }
        subscription = await withDeadline(
          active.pushManager.subscribe(options),
          VISITOR_PUSH_DEADLINE_MS,
          null,
        );
      } catch (error) {
        this.permission = this.readPermission();
        return this.turnOnFailed();
      }
      if (!subscription || deadlineExpired(subscription)) {
        return this.turnOnFailed();
      }

      const verdict = await this.storeOnServer(subscription);

      if (verdict.outcome === SUCCESS) {
        this.subscription = subscription;
        this.hasSubscription = true;
        this.known = true;
        // The landing, and where the note about an owed cleanup goes. This visitor is
        // subscribed again, so "they are on their way out" is no longer true, and onRetry may
        // not read it that way. Kept until here rather than dropped at the press, because a
        // press that never lands must not throw the note away.
        this.unconfirmedEndpoint = null;
        this.busy = null;
        return null;
      }

      if (verdict.outcome === TIMEOUT) {
        // The server may well have stored it; we simply cannot say. Rolling back here would
        // throw away a subscription that might be working, so the subscription is kept and the
        // face reads differently from one we can vouch for.
        this.subscription = subscription;
        this.hasSubscription = true;
        this.known = null;
        // Same here. We cannot say whether the server stored it, but the browser is holding a
        // live subscription either way, so this visitor is not leaving.
        this.unconfirmedEndpoint = null;
        this.busy = null;
        return null;
      }

      // The server refused. No orphans: a browser holding a live subscription nothing on the
      // server knows about is the state this whole component exists to remove.
      return this.turnOnFailed(subscription);
    },

    /**
     * Hands the subscription to the server, and does not give up on the first silence.
     *
     * A TIMEOUT is the one verdict that says nothing about what the server did. It may well hold
     * the row; we simply were not told in time. On a first click that matters more than anywhere
     * else, because the alternative is a face that reads "not confirmed" over a subscription that
     * is in fact stored, and the desk would then send to a browser whose owner was told it was
     * not signed up. So a silence is asked again inside the same click, within a budget spent in
     * whole attempts.
     *
     * Asking twice cannot store twice: the endpoint keys on endpoint_hash and stores through
     * firstOrNew. A refusal is never retried, because a refusal is an answer.
     */
    async storeOnServer(subscription) {
      const keys = this.keysOf(subscription);
      const until = Date.now() + VISITOR_PUSH_STORE_BUDGET_MS;
      let verdict;

      do {
        verdict = await subscribeOnServer({
          endpoint: subscription.endpoint,
          p256dh: keys.p256dh,
          auth: keys.auth,
          websiteId: this.websiteId,
        });
      } while (
        verdict.outcome === TIMEOUT
        && !this.gone
        && Date.now() + VISITOR_PUSH_DEADLINE_MS <= until
      );

      return verdict;
    },

    /**
     * Give back whatever the browser handed us and record that the attempt failed. The rollback
     * is best effort and its outcome is measured rather than assumed: a browser that refuses to
     * unsubscribe leaves a real orphan, and that is the repair state, not a silent off.
     */
    async turnOnFailed(subscription) {
      let stillHeld = false;
      if (subscription) {
        stillHeld = !(await this.unsubscribeInBrowser(subscription));
      }
      this.subscription = stillHeld ? subscription : null;
      this.hasSubscription = stillHeld;
      this.known = false;
      this.lastTurnOnFailed = true;
      this.busy = null;

      return null;
    },

    async turnOff() {
      this.freezeControls();
      this.busy = 'turning-off';
      this.lastTurnOnFailed = false;
      this.lastTurnOffFailed = false;
      this.browserUnreadable = false;

      // EXIT-A never registers, and turning off is reachable from it: the way out is the whole
      // reason that mode draws anything. ensureRegistration installs a worker when there is none,
      // so asking it here would put one on the machine of a viewer this host offers nothing to,
      // at the moment they press the button that removes them. Unreachable today only because
      // the probe has already kept the registration it found, which is a circumstance and not a
      // rule. Same for a server with no keys: nothing may be installed for a control that can
      // never be offered.
      const registration = this.bare
        ? this.registration
        : await this.ensureRegistration();
      let subscription = this.subscription;
      if (!subscription && registration) {
        subscription = await this.heldSubscription(registration);
        if (subscription === UNREADABLE) {
          // Unless the browser half is already done. An unconfirmed endpoint means this browser
          // let go and the server did not answer, and that endpoint is the only thing this read
          // was for. Refusing to go on would leave somebody who pressed off pressing a retry
          // that can never do anything, waiting on a question this operation does not ask.
          if (! this.unconfirmedEndpoint) {
            this.browserUnreadable = true;
            this.busy = null;
            return;
          }
          subscription = null;
        }
      }

      // The endpoint is read before anything is torn down, because the server still has to be
      // told which row to forget after the browser has dropped its own copy. When the browser
      // half is already done, that endpoint is the only thing left to say it with.
      const endpoint = subscription ? subscription.endpoint : this.unconfirmedEndpoint;
      if (!endpoint) {
        this.hasSubscription = false;
        this.known = false;
        this.busy = null;
        return;
      }

      const [serverVerdict, browserGone] = await Promise.all([
        deleteOnServer({ endpoint, websiteId: this.websiteId }),
        subscription ? this.unsubscribeInBrowser(subscription) : Promise.resolve(true),
      ]);

      this.hasSubscription = !browserGone;
      // A browser that would not let go leaves this visitor on repair, and they got there by
      // asking to stop. Remembering which direction it was is what stops the retry button from
      // turning them back on.
      this.lastTurnOffFailed = !browserGone;
      this.subscription = browserGone ? null : subscription;
      // A refused or unanswered delete leaves the server half unknown. Only a delete that came
      // back in the pinned shape proves the row is gone.
      this.known = serverVerdict.outcome === SUCCESS ? false : null;
      // Remember the endpoint exactly as long as the server half is unfinished.
      this.unconfirmedEndpoint = browserGone && this.known === null ? endpoint : null;
      this.busy = null;
    },

    async unsubscribeInBrowser(subscription) {
      try {
        const result = await withDeadline(
          subscription.unsubscribe(),
          VISITOR_PUSH_DEADLINE_MS,
          null,
        );
        if (deadlineExpired(result)) {
          return false;
        }
        return result !== false;
      } catch (error) {
        return false;
      }
    },

    async recheck() {
      if (!this.subscription) {
        this.hasSubscription = false;
        this.known = false;
        return;
      }
      this.freezeControls();
      this.rechecking = true;
      const generation = this.pressGeneration;
      try {
        const answer = await this.askServer(this.subscription.endpoint);

        // Only if nothing was pressed while this was in flight. Recheck is the slow call by
        // construction, and turning off during it used to end with this line putting the old
        // answer back: the delete had landed, the server had confirmed it, and the visitor was
        // then told the server had not confirmed it yet. The press decides, not the read that
        // was already on its way.
        if (generation === this.pressGeneration) {
          this.known = answer;
        }
      } finally {
        this.rechecking = false;
      }
    },

    keysOf(subscription) {
      if (typeof subscription.toJSON === 'function') {
        const json = subscription.toJSON();
        if (json && json.keys) {
          return { p256dh: json.keys.p256dh, auth: json.keys.auth };
        }
      }
      return {
        p256dh: this.encodeKey(subscription, 'p256dh'),
        auth: this.encodeKey(subscription, 'auth'),
      };
    },

    /** The fallback for a subscription with no toJSON, which the specification does not promise. */
    encodeKey(subscription, name) {
      if (typeof subscription.getKey !== 'function') {
        return null;
      }

      // One conversion, used here and by the key comparison. Two of them drift, and the way they
      // drift is that one of them starts disagreeing with the browser about what a key is.
      return this.base64UrlOf(subscription.getKey(name));
    },

    /**
     * Give back a subscription that was made against a different key, before asking for a new one.
     *
     * The browser refuses to make a second subscription while it holds one, and it refuses a
     * different key on the one it holds. Without this, the visitor whose keys rotated presses the
     * button, the browser throws, and they land in repair permanently with no way out of it.
     */
    async releaseForeignSubscription(registration) {
      const subscription = await this.heldSubscription(registration);
      if (!subscription || subscription === UNREADABLE || this.subscriptionMatchesKey(subscription)) {
        return;
      }

      await this.unsubscribeInBrowser(subscription);
      this.subscription = null;
      this.hasSubscription = false;
      this.subscriptionForAnotherKey = false;
    },

    /**
     * Whether what the browser holds was made against the key this page is handing out.
     *
     * A push subscription is bound to one application server key. Rotate the pair and every
     * subscription made under the old one is unreachable: this server signs with the new key and
     * the push service refuses. Nothing about the subscription says so. It is still there, the
     * endpoint still resolves, the row on the server is still real, so the control reads "on" and
     * the server agrees, and notifications simply never arrive again.
     *
     * The browser will also refuse to make a new subscription while the old one is held, so a
     * visitor in that state cannot fix it by pressing the button either.
     *
     * When the browser does not report the key, which older ones do not, this says nothing rather
     * than guessing. An unknown is not a mismatch.
     */
    subscriptionMatchesKey(subscription) {
      if (!this.vapidPublicKey) {
        return true;
      }
      const options = subscription ? subscription.options : null;
      const held = options ? options.applicationServerKey : null;
      if (!held) {
        return true;
      }

      // Bytes we cannot read are an unknown too, and an unknown is not a mismatch. A browser that
      // reports something other than the buffer the specification describes would otherwise be
      // told its perfectly good subscription is broken, and told it on every page view.
      const theirs = this.base64UrlOf(held);
      if (!theirs) {
        return true;
      }

      return theirs === String(this.vapidPublicKey).replace(/=+$/, '');
    },

    /** The url safe base64 of an ArrayBuffer, without padding, which is how VAPID keys travel. */
    base64UrlOf(buffer) {
      if (!buffer) {
        return null;
      }
      try {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }

        return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      } catch (error) {
        return null;
      }
    },

    urlBase64ToUint8Array(base64String) {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    },
  },
};
</script>

<style scoped>
/*
 * In place and quiet. No fixed position, no z-index, nothing that can sit over the page.
 *
 * The layout itself is gone from here. It used to be a flex row that put the button first, which
 * is the opposite order of the banner this now lives in, and two layouts fighting over the same
 * element is how a button ends up beside a heading it belongs under. _guest-cta.scss owns the
 * arrangement; what is left here is the part that is about state rather than shape.
 */
.visitor-notifications {
  margin: 1rem 0;
}

/* The exit for somebody already subscribed on a brand that offers nothing. No heading, no icon,
 * no invitation: a sentence and the way out, which is what it is. */
.visitor-notifications--bare >>> .guest-cta__container {
  border: 0;
  box-shadow: none;
  padding: 0;
  background: transparent;
}

.visitor-notifications--bare >>> .guest-cta__icon-holder {
  display: none;
}

/* Unverified has to read differently from on, so it is not only a different sentence. */
.visitor-notifications[data-state='on-unverified'] .visitor-notifications__status,
.visitor-notifications[data-state='off-cleanup-pending'] .visitor-notifications__status,
.visitor-notifications[data-state='repair'] .visitor-notifications__status {
  font-style: italic;
  opacity: 0.85;
}

.visitor-notifications__notice {
  max-width: 42rem;
}

/*
 * A button that cannot be used right now has to look like it.
 *
 * While the control is busy the toggle says aria-disabled rather than disabled, so that pressing it
 * does not cost somebody their place on the page: a disabled button that has focus hands that focus
 * to the document body. The browser's own greying out goes with the attribute, so it is drawn here
 * instead. The sentence underneath is the real feedback; this is the part somebody sees before they
 * read it.
 */
.visitor-notifications__toggle[aria-disabled='true'],
.visitor-notifications__retry[aria-disabled='true'] {
  opacity: 0.6;
  cursor: default;
}
</style>
